import { CheckCircle2, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";

const plans = [
  {
    name: "Standard SaaS",
    description: "For small deployments",
    highlight: false,
    features: {
      stations: "Up to 25",
      users: "3 admin users",
      mobile: "Standard EvGateway app",
      portal: "Standard",
      api: false,
      whitelabel: false,
      dedicatedCloud: false,
      thirdPartyIntegrations: false,
      support: "Email, Phone - Business Hours",
      sla: "99%"
    }
  },
  {
    name: "White Label",
    description: "For growing networks",
    highlight: true,
    features: {
      stations: "Up to 250",
      users: "10 admin users",
      mobile: "Customer brand",
      portal: "Basic white label",
      api: true,
      whitelabel: "Partial",
      dedicatedCloud: false,
      thirdPartyIntegrations: false,
      support: "Priority",
      sla: "99.5%"
    }
  },
  {
    name: "Enterprise",
    description: "For large operations",
    highlight: false,
    features: {
      stations: "Unlimited",
      users: "Unlimited",
      mobile: "White-label - Customizable",
      portal: "Full white-label",
      api: true,
      whitelabel: "Full",
      dedicatedCloud: true,
      thirdPartyIntegrations: true,
      support: "24/7 Dedicated",
      sla: "99.9%"
    }
  }
];

const featureRows = [
  { key: "stations", label: "Charging Stations" },
  { key: "users", label: "Admin Users" },
  { key: "mobile", label: "Mobile Apps" },
  { key: "portal", label: "Web Portal" },
  { key: "api", label: "API Access" },
  { key: "whitelabel", label: "White-Label" },
  { key: "support", label: "Support" },
  { key: "sla", label: "Uptime SLA" },
];

export function FeatureComparisonNew() {
  return (
    <section id="features" className="py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-card/40" />
      <div className="absolute top-0 left-0 w-full section-divider" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <ScrollReveal className="text-center mb-16">
          <motion.div 
            className="inline-flex items-center gap-2 badge-primary mb-6"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Pricing
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            Choose Your <span className="text-gradient">Plan</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto font-light">
            Flexible plans that scale with your business. All plans include core platform features.
          </p>
        </ScrollReveal>

        {/* Plans Grid */}
        <StaggerContainer className="grid md:grid-cols-3 gap-6 mb-20" staggerDelay={0.1}>
          {plans.map((plan, idx) => (
            <StaggerItem key={idx}>
              <motion.div
                whileHover={{ scale: 1.02, y: -8 }}
                transition={{ duration: 0.3 }}
                className={`rounded-3xl p-8 md:p-10 h-full ${
                  plan.highlight 
                    ? 'bg-gradient-to-br from-primary/15 via-primary/10 to-primary/5 border-2 border-primary/40 shadow-lg shadow-primary/10' 
                    : 'glass-card'
                }`}
              >
                {plan.highlight && (
                  <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider bg-primary text-primary-foreground px-4 py-1.5 rounded-full mb-6">
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl md:text-3xl font-bold mb-2 tracking-tight text-white">{plan.name}</h3>
                <p className="text-sm text-white/60 mb-8 font-light">{plan.description}</p>
                
                <div className="space-y-4 mb-10">
                  {featureRows.map((row) => {
                    const value = plan.features[row.key as keyof typeof plan.features];
                    return (
                      <div key={row.key} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                        <span className="text-sm text-white/60">{row.label}</span>
                        <span className="text-sm font-medium text-white">
                          {typeof value === 'boolean' ? (
                            value ? (
                              <CheckCircle2 className="h-5 w-5 text-primary" />
                            ) : (
                              <X className="h-5 w-5 text-white/20" />
                            )
                          ) : (
                            value
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <Button 
                  variant={plan.highlight ? "hero" : "outline"} 
                  className="w-full py-6 text-base"
                  asChild
                >
                  <Link to="/contact">
                    {plan.highlight ? "Start Free Trial" : "Contact Sales"}
                  </Link>
                </Button>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Enterprise Callout */}
        <ScrollReveal>
          <motion.div 
            className="bg-gradient-to-r from-primary/10 via-card to-accent/10 border border-border/40 rounded-3xl p-10 md:p-14 text-center"
            whileHover={{ scale: 1.005 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-white">
              Need a Custom Solution?
            </h3>
            <p className="text-white/60 max-w-2xl mx-auto mb-8 text-lg font-light">
              Our enterprise team will work with you to create a tailored package that meets your specific requirements, integration needs, and scale.
            </p>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button variant="hero" size="lg" className="shadow-lg shadow-primary/20 text-base px-10 py-6" asChild>
                <Link to="/contact">Talk to Enterprise Team</Link>
              </Button>
            </motion.div>
          </motion.div>
        </ScrollReveal>
      </div>

      <div className="absolute bottom-0 left-0 w-full section-divider" />
    </section>
  );
}